"use client";

import { ArrowRight, Globe, Phone } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { ROUTES } from "@/lib/routes";
import {
  BLOG_CTA_ACCENT_HEX,
  BLOG_CTA_GRADIENT_HEX,
  ThemeName,
} from "@/components/v3/theme";
import { BookingServiceType } from "@/constants/blogServiceCategories";
import { trackCallClicked, trackBookAppointmentClicked } from "@/lib/analytics";

type AvatarConfig = {
  src: string;
  size: number;
  style: React.CSSProperties;
  anim: string;
  delay: string;
};

const avatars: AvatarConfig[] = [
  {
    src: "/images/booking/Item.png",
    size: 90,
    style: { top: "-18%", left: "0%" },
    anim: "animate-float",
    delay: "0s",
  },
  {
    src: "/images/booking/Item4.png",
    size: 80,
    style: { top: "-14%", right: "5%" },
    anim: "animate-float-alt",
    delay: "0.8s",
  },
  {
    src: "/images/booking/Item6.png",
    size: 120,
    style: { bottom: "-30%", left: "4%" },
    anim: "animate-float-slow",
    delay: "1.5s",
  },
  {
    src: "/images/booking/Item12.png",
    size: 100,
    style: { bottom: "-30%", right: "10%" },
    anim: "animate-float-fast",
    delay: "0.4s",
  },
];

interface CTABannerProps {
  /** Colour the banner follows — comes from the post's service category. */
  theme?: Exclude<ThemeName, "white">;
  /** Service pre-selected in the booking flow when the CTA is clicked. */
  bookingService?: BookingServiceType;
}

const CTABanner = ({ theme = "blue", bookingService }: CTABannerProps) => {
  const gradient = BLOG_CTA_GRADIENT_HEX[theme];
  const accentHex = BLOG_CTA_ACCENT_HEX[theme];

  const bookingUrl = bookingService
    ? `${ROUTES.booking}?service=${bookingService}`
    : ROUTES.booking;

  return (
    <section
      className="my-6 overflow-hidden rounded-2xl"
      style={
        {
          backgroundImage: `linear-gradient(to bottom, ${gradient.from}, ${gradient.to})`,
          "--cta-glow": `${accentHex}59`,
        } as React.CSSProperties
      }
    >
      <div className="relative flex flex-col items-stretch justify-between lg:flex-row lg:items-center">
        {/* Floating avatar bubbles + Book Now */}
        <div className="relative hidden h-56 w-[44%] shrink-0 lg:block">
          {avatars.map((avatar, i) => (
            <div
              key={i}
              className={`absolute ${avatar.anim} origin-center`}
              style={{
                ...avatar.style,
                animationDelay: avatar.delay,
                width: avatar.size,
                height: avatar.size,
              }}
            >
              <div
                className="relative overflow-hidden rounded-full border-2 border-white/25 shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
                style={{ width: avatar.size, height: avatar.size }}
              >
                <Image
                  src={avatar.src}
                  alt="Client"
                  fill
                  quality={100}
                  className="object-cover"
                  sizes={`${avatar.size}px`}
                />
              </div>
            </div>
          ))}

          <Link
            href={bookingUrl}
            className="group absolute left-1/2 top-1/2 z-10 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2.5 overflow-hidden rounded-xl bg-white px-6 py-3 text-base font-semibold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_var(--cta-glow)] md:gap-3 md:px-9 md:py-4"
            style={{ color: accentHex }}
            aria-label="Book Now"
            onClick={() =>
              trackBookAppointmentClicked({
                location: "blog_cta_banner",
                service: bookingService,
              })
            }
          >
            <span className="relative z-10 whitespace-nowrap">Book Now</span>
            <ArrowRight
              className="relative z-10 h-5 w-5 shrink-0 transition-transform duration-300 group-hover:translate-x-1"
              aria-hidden
            />
            <span
              className="absolute -inset-2 -translate-x-full skew-x-12 transition-transform duration-700 group-hover:translate-x-full"
              style={{ backgroundColor: `${accentHex}66` }}
            />
          </Link>
        </div>

        {/* Identity + contact */}
        <div className="flex flex-grow flex-col p-6 text-center text-white sm:p-8 lg:py-8 lg:pl-0 lg:pr-10 lg:text-left">
          <h2 className="text-xl leading-tight sm:text-2xl md:text-3xl lg:text-4xl xl:text-[56px]">
            <span className="relative inline-block -translate-y-1 font-sorts-mill-goudy font-light tracking-tighter">
              Alexander
            </span>{" "}
            <span className="font-virtual text-2xl font-normal sm:text-3xl md:text-4xl lg:text-5xl xl:text-8xl">
              Rodriguez
            </span>
          </h2>

          <p className="font-virtual text-3xl leading-tight text-secondary lg:mt-2 lg:text-4xl">
            Seize the Day, Book an Appointment Today
          </p>

          <p className="mx-auto mt-2 max-w-xl text-[0.7rem] leading-relaxed text-white/95 sm:text-xs lg:mx-0 lg:text-sm">
            Reach out anytime. Book online, call us, or visit our office in
            person.
          </p>

          {/* Inline CTA Button below lg breakpoint */}
          <div className="mt-4 lg:hidden">
            <Link
              href={bookingUrl}
              className="group inline-flex items-center gap-2 overflow-hidden rounded-lg bg-white px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_var(--cta-glow)]"
              style={{ color: accentHex }}
              aria-label="Book Now"
              onClick={() =>
                trackBookAppointmentClicked({
                  location: "blog_cta_banner",
                  service: bookingService,
                })
              }
            >
              <span className="relative z-10 whitespace-nowrap">Book Now</span>
              <ArrowRight
                className="relative z-10 h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden
              />
              <span
                className="absolute -inset-2 -translate-x-full skew-x-12 transition-transform duration-700 group-hover:translate-x-full"
                style={{ backgroundColor: `${accentHex}66` }}
              />
            </Link>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[0.7rem] font-semibold uppercase tracking-wide text-white/90 sm:text-xs lg:justify-start lg:text-sm">
            <span>Counselling</span>
            <span>Allied Health</span>
            <span>Life Coach</span>
          </div>

          <div className="mt-3 w-full border-t border-white/20" />

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[0.75rem] font-medium text-white/85 sm:text-xs lg:justify-start lg:text-sm">
            <a
              href="tel:+611300467513"
              className="flex items-center gap-2 transition-colors duration-200 hover:text-white"
              onClick={() => trackCallClicked({ location: "cta_banner" })}
            >
              <span className="flex shrink-0 items-center justify-center rounded-full bg-white p-1">
                <Phone
                  fill="currentColor"
                  className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5"
                  style={{ color: accentHex }}
                />
              </span>
              <span className="hover:underline">1300 467 513</span>
            </a>
            <a
              href="https://www.alexrodriguez.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 transition-colors duration-200 hover:text-white"
            >
              <span className="flex shrink-0 items-center justify-center rounded-full bg-white p-1">
                <Globe
                  className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5"
                  style={{ color: accentHex }}
                />
              </span>
              <span className="hover:underline">www.alexrodriguez.com.au</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTABanner;
